<template>
  <div :class="$style.topicChipSelect">
    <div :class="$style.topicChips">
      <Chip
        v-for="topic in availableTopics"
        :key="topic.id"
        :label="topic.name"
        :selected="selectedTopicId === topic.id"
        :color="getTopicColor(topic.name)"
        @click="selectTopic(topic.id)"
      />
      <Chip label="+ New Topic" variant="dashed" @click="showNewTopicDialog" />
    </div>
    <TopicDialog
      v-if="showDialog"
      :is-open="showDialog"
      :topic-id="editingTopicId"
      @close="closeDialog"
      @save="handleTopicSave"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { topicService } from "../services/topicService";
import TopicDialog from "./TopicDialog.vue";
import Chip from "./Chip.vue";

const props = defineProps<{
  modelValue: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", topicId: string): void;
}>();

const availableTopics = computed(() =>
  topicService.getTopics().filter((topic) => !topic.system)
);
const selectedTopicId = ref(props.modelValue);
const showDialog = ref(false);
const editingTopicId = ref<string>();

watch(
  () => props.modelValue,
  (newValue) => {
    selectedTopicId.value = newValue;
  }
);

function selectTopic(topicId: string) {
  // If clicking on the already selected topic, deselect it
  if (selectedTopicId.value === topicId) {
    selectedTopicId.value = "";
    emit("update:modelValue", "");
  } else {
    selectedTopicId.value = topicId;
    emit("update:modelValue", topicId);
  }
}

function showNewTopicDialog() {
  editingTopicId.value = undefined;
  showDialog.value = true;
}

function closeDialog() {
  showDialog.value = false;
  editingTopicId.value = undefined;
}

function handleTopicSave(topicId: string) {
  emit("update:modelValue", topicId);
}

function getTopicColor(topicName: string): string | undefined {
  const colorMap: Record<string, string> = {
    Coding: "var(--primary-color)",
    Meeting: "var(--task-design)",
    Planning: "var(--task-meeting)",
    Research: "var(--task-research)",
    Documentation: "var(--task-break)",
    Other: "var(--task-other)",
    "In-Between": "var(--task-break)",
  };
  return colorMap[topicName];
}
</script>

<style module>
.topicChipSelect {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.topicChips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
</style>
